import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertBetaAccess } from "@/lib/beta-access";
import { assertCanManage, requireCompany } from "@/lib/company-access";

// Nuvatr connection for Reality Twin subscribers: connect once, then the Twin's
// knowledge graph keeps the Nuvatr profile in sync. Everything here runs behind
// an active-subscription check, because that is exactly what the benefit is
// tied to on the /nuvatr page.

export type NuvatrStatus = {
  eligible: boolean;
  reason: "ok" | "no_subscription";
  canManage: boolean;
  configured: boolean;
  connection: {
    status: string;
    nuvatrId: string | null;
    profileUrl: string | null;
    discountCode: string | null;
    lastSyncedAt: string | null;
    lastError: string | null;
  } | null;
  listingPriceChf: number;
  listPriceChf: number;
  discountPercent: number;
};

/** Active paying subscription = the gate for every Nuvatr benefit. */
async function hasActiveSubscription(supabaseAdmin: any, userId: string): Promise<boolean> {
  const { data } = await supabaseAdmin
    .from("subscriptions")
    .select("status, current_period_end")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(5);
  const now = Date.now();
  const hasSubRow = (data ?? []).some((s: { status: string; current_period_end: string | null }) => {
    const end = s.current_period_end ? new Date(s.current_period_end).getTime() : null;
    const notExpired = !end || end > now;
    if (["active", "trialing", "past_due"].includes(s.status) && notExpired) return true;
    return s.status === "canceled" && end !== null && end > now;
  });
  if (hasSubRow) return true;
  // Billing everywhere else in the app reads the plan off the user's twins
  // (see usage.server.ts). Any paid plan there counts as an active plan, so
  // the Nuvatr gate matches what the sidebar and Billing page show.
  const { data: twins } = await supabaseAdmin
    .from("companies")
    .select("plan")
    .eq("user_id", userId);
  const { bestPlan } = await import("@/lib/plan-limits");
  return bestPlan((twins ?? []).map((t: { plan: string | null }) => t.plan)) !== "free";
}


export const getNuvatrStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<NuvatrStatus> => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const {
      NUVATR_LISTING_PRICE_CHF,
      NUVATR_LIST_PRICE_CHF,
      NUVATR_DISCOUNT_PERCENT,
    } = await import("@/lib/nuvatr.server");

    const eligible = await hasActiveSubscription(supabaseAdmin, context.userId);
    const { data: row } = await supabaseAdmin
      .from("nuvatr_connections")
      .select("status, nuvatr_id, profile_url, discount_code, last_synced_at, last_error")
      .eq("company_id", access.id)
      .maybeSingle();

    return {
      eligible,
      reason: eligible ? "ok" : "no_subscription",
      canManage: access.role !== "member",
      configured: Boolean(process.env["NUVATR_PARTNER_SECRET"]),
      connection: row
        ? {
            status: row.status,
            nuvatrId: row.nuvatr_id,
            profileUrl: row.profile_url,
            discountCode: row.discount_code,
            lastSyncedAt: row.last_synced_at,
            lastError: row.last_error,
          }
        : null,
      listingPriceChf: NUVATR_LISTING_PRICE_CHF,
      listPriceChf: NUVATR_LIST_PRICE_CHF,
      discountPercent: NUVATR_DISCOUNT_PERCENT,
    };
  });

/** The company data that will be published — editable before it goes live. */
export type NuvatrDraft = {
  name: string;
  website: string | null;
  description: string | null;
  industry: string | null;
  country: string | null;
  size: string | null;
  services: string[];
  seeking: string[];
};

export const getNuvatrDraft = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<NuvatrDraft> => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("companies")
      .select("name, website, description, industry, country, size, service_categories, seeking")
      .eq("id", access.id)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error("Company not found.");
    return {
      name: data.name,
      website: data.website,
      description: data.description,
      industry: data.industry,
      country: data.country,
      size: data.size,
      services: data.service_categories ?? [],
      seeking: data.seeking ?? [],
    };
  });

/** Create or refresh the Nuvatr listing from the Twin's knowledge graph. */
export const syncNuvatr = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data?: { profile?: Partial<NuvatrDraft> }) => data ?? {})
  .handler(async ({ data, context }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    assertCanManage(access);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (!(await hasActiveSubscription(supabaseAdmin, context.userId))) {
      throw new Error("Nuvatr benefits require an active Reality Twin subscription.");
    }

    // The customer reviews (and may correct) their company data before it is
    // published to the registry — save those edits first, so Twin and Nuvatr
    // never disagree about what the company says about itself.
    const p = data.profile;
    if (p) {
      const patch: {
        name?: string;
        website?: string | null;
        description?: string | null;
        industry?: string | null;
        country?: string | null;
        size?: string | null;
        service_categories?: string[];
        seeking?: string[];
      } = {};
      if (typeof p.name === "string" && p.name.trim()) patch.name = p.name.trim();
      if (p.website !== undefined) patch.website = p.website || null;
      if (p.description !== undefined) patch.description = p.description || null;
      if (p.industry !== undefined) patch.industry = p.industry || null;
      if (p.country !== undefined) patch.country = p.country || null;
      if (p.size !== undefined) patch.size = p.size || null;
      if (p.services !== undefined) patch.service_categories = p.services;
      if (p.seeking !== undefined) patch.seeking = p.seeking;
      if (Object.keys(patch).length > 0) {
        const { error } = await context.supabase.from("companies").update(patch).eq("id", access.id);
        if (error) throw error;
      }

    }

    const { buildProfilePayload, callNuvatr, payloadHash, NUVATR_DISCOUNT_PERCENT } = await import(
      "@/lib/nuvatr.server"
    );
    const email = (context.claims as { email?: string } | null)?.email ?? null;
    const payload = await buildProfilePayload(access.id, email);


    try {
      const result = await callNuvatr("upsert", payload);
      const { error } = await supabaseAdmin.from("nuvatr_connections").upsert(
        {
          company_id: access.id,
          user_id: context.userId,
          status: "connected",
          nuvatr_id: result.nuvatr_id ?? null,
          nuvatr_slug: result.slug ?? null,
          profile_url: result.profile_url ?? null,
          discount_code: result.discount_code ?? null,
          discount_percent: NUVATR_DISCOUNT_PERCENT,
          last_synced_at: new Date().toISOString(),
          last_error: null,
          payload_hash: payloadHash(payload),
        },
        { onConflict: "company_id" },
      );
      if (error) throw error;
      return { ok: true as const, nuvatrId: result.nuvatr_id ?? null, profileUrl: result.profile_url ?? null };
    } catch (e) {
      const message = (e as Error).message;
      await supabaseAdmin.from("nuvatr_connections").upsert(
        {
          company_id: access.id,
          user_id: context.userId,
          status: "error",
          last_error: message,
        },
        { onConflict: "company_id" },
      );
      throw new Error(message);
    }
  });

/** Unlink the profile. Nuvatr keeps the listing but stops receiving updates. */
export const disconnectNuvatr = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    assertCanManage(access);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { callNuvatr } = await import("@/lib/nuvatr.server");
    try {
      await callNuvatr("disconnect", { external_id: access.id });
    } catch (e) {
      console.error("[nuvatr] disconnect notice failed:", (e as Error).message);
    }
    await supabaseAdmin.from("nuvatr_connections").delete().eq("company_id", access.id);
    return { ok: true as const };
  });
